import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, Eye, Zap, ArrowRight, ChevronDown, Mail, Sparkles, Check } from 'lucide-react';

// Animated cyber grid background
const CyberGrid = () => {
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Animated grid lines */}
      <svg className="absolute inset-0 w-full h-full opacity-20">
        <defs>
          <pattern id="cyber-grid" width="50" height="50" patternUnits="userSpaceOnUse">
            <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#10b981" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#cyber-grid)" />
      </svg>
      
      {/* Floating particles */}
      {[...Array(30)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 bg-[#10b981] rounded-full"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, -30, 0],
            opacity: [0.2, 1, 0.2],
            scale: [1, 1.5, 1],
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2,
          }}
        />
      ))}
    </div>
  );
};

// Animated shield logo
const AnimatedShield = ({ isVisible }) => {
  return (
    <motion.div
      className="relative w-40 h-48 md:w-56 md:h-64"
      initial={{ scale: 0, rotate: -180 }}
      animate={isVisible ? { scale: 1, rotate: 0 } : {}}
      transition={{ type: "spring", stiffness: 100, damping: 15, duration: 1.2 }}
    >
      <svg viewBox="0 0 100 120" className="w-full h-full drop-shadow-2xl">
        {/* Shield base with gradient */}
        <defs>
          <linearGradient id="shieldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#2a6a7e" />
            <stop offset="100%" stopColor="#1a3a52" />
          </linearGradient>
          <linearGradient id="shieldGradLight" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3a8a9e" />
            <stop offset="100%" stopColor="#2a5a72" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* Main shield shape */}
        <motion.path
          d="M50 5 L90 20 L90 55 C90 85 50 115 50 115 C50 115 10 85 10 55 L10 20 Z"
          fill="url(#shieldGrad)"
          stroke="#3a8a9e"
          strokeWidth="2"
          initial={{ pathLength: 0 }}
          animate={isVisible ? { pathLength: 1 } : {}}
          transition={{ duration: 2, ease: "easeInOut" }}
        />
        
        {/* Light side of shield */}
        <motion.path
          d="M50 5 L50 115 C50 115 10 85 10 55 L10 20 Z"
          fill="url(#shieldGradLight)"
          initial={{ opacity: 0 }}
          animate={isVisible ? { opacity: 1 } : {}}
          transition={{ delay: 0.5, duration: 1 }}
        />
        
        {/* Circuit lines inside shield */}
        <motion.path
          d="M35 35 L35 55 L50 55"
          fill="none"
          stroke="#10b981"
          strokeWidth="4"
          strokeLinecap="round"
          filter="url(#glow)"
          initial={{ pathLength: 0 }}
          animate={isVisible ? { pathLength: 1 } : {}}
          transition={{ delay: 1, duration: 0.8 }}
        />
        <motion.path
          d="M50 35 L50 75"
          fill="none"
          stroke="#5ec4a8"
          strokeWidth="4"
          strokeLinecap="round"
          filter="url(#glow)"
          initial={{ pathLength: 0 }}
          animate={isVisible ? { pathLength: 1 } : {}}
          transition={{ delay: 1.3, duration: 0.8 }}
        />
        <motion.path
          d="M65 45 L65 65 L50 65"
          fill="none"
          stroke="#10b981"
          strokeWidth="4"
          strokeLinecap="round"
          filter="url(#glow)"
          initial={{ pathLength: 0 }}
          animate={isVisible ? { pathLength: 1 } : {}}
          transition={{ delay: 1.6, duration: 0.8 }}
        />
        
        {/* Connection nodes */}
        <motion.circle
          cx="35" cy="35" r="4"
          fill="#10b981"
          filter="url(#glow)"
          initial={{ scale: 0 }}
          animate={isVisible ? { scale: 1 } : {}}
          transition={{ delay: 1.8, type: "spring" }}
        />
        <motion.circle
          cx="65" cy="45" r="4"
          fill="#5ec4a8"
          filter="url(#glow)"
          initial={{ scale: 0 }}
          animate={isVisible ? { scale: 1 } : {}}
          transition={{ delay: 2, type: "spring" }}
        />
        <motion.circle
          cx="50" cy="75" r="4"
          fill="#10b981"
          filter="url(#glow)"
          initial={{ scale: 0 }}
          animate={isVisible ? { scale: 1 } : {}}
          transition={{ delay: 2.2, type: "spring" }}
        />
      </svg>
      
      {/* Pulsing rings around shield */}
      <motion.div
        className="absolute inset-0 border-2 border-[#10b981]/30 rounded-full"
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.5, 0, 0.5],
        }}
        transition={{ duration: 2, repeat: Infinity }}
      />
    </motion.div>
  );
};

// Typing effect component
const TypeWriter = ({ text, delay = 0, speed = 50, onComplete }) => {
  const [displayText, setDisplayText] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  
  useEffect(() => {
    const timeout = setTimeout(() => {
      let index = 0;
      const interval = setInterval(() => {
        if (index < text.length) {
          setDisplayText(text.slice(0, index + 1));
          index++;
        } else {
          clearInterval(interval);
          setIsComplete(true);
          onComplete && onComplete();
        }
      }, speed);
      return () => clearInterval(interval);
    }, delay);
    return () => clearTimeout(timeout);
  }, [text, delay, speed, onComplete]);
  
  return (
    <span>
      {displayText}
      {!isComplete && (
        <motion.span
          className="inline-block w-0.5 h-6 md:h-8 bg-[#10b981] ml-1"
          animate={{ opacity: [1, 0, 1] }}
          transition={{ duration: 0.8, repeat: Infinity }}
        />
      )}
    </span>
  );
};

// Stats counter component
const StatCounter = ({ value, suffix = '', label, delay = 0 }) => {
  const [count, setCount] = useState(0);
  const numValue = parseInt(value.replace(/\D/g, ''));
  
  useEffect(() => {
    const timeout = setTimeout(() => {
      let start = 0;
      const end = numValue;
      const duration = 2000;
      const increment = end / (duration / 16);
      
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);
      
      return () => clearInterval(timer);
    }, delay);
    
    return () => clearTimeout(timeout);
  }, [numValue, delay]);
  
  return (
    <motion.div
      className="text-center"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: delay / 1000, duration: 0.5 }}
    >
      <div className="text-3xl md:text-4xl font-bold text-[#10b981]">
        {value.includes('%') ? `${count}%` : value.includes('+') ? `+${count}` : count}
        {suffix}
      </div>
      <div className="text-sm text-gray-400 mt-1">{label}</div>
    </motion.div>
  );
};

// Floating feature icons
const FloatingIcon = ({ icon: Icon, label, delay, position }) => {
  return (
    <motion.div
      className={`absolute ${position} flex flex-col items-center gap-2`}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, type: "spring", stiffness: 100 }}
    >
      <motion.div
        className="w-12 h-12 md:w-16 md:h-16 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 flex items-center justify-center"
        animate={{
          y: [0, -10, 0],
        }}
        transition={{ duration: 3, repeat: Infinity, delay: delay * 0.5 }}
      >
        <Icon className="w-6 h-6 md:w-8 md:h-8 text-[#10b981]" />
      </motion.div>
      <span className="text-xs text-gray-400 hidden md:block">{label}</span>
    </motion.div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [showContent, setShowContent] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showCTA, setShowCTA] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const containerRef = useRef(null);
  
  useEffect(() => {
    // Sequence of animations
    setTimeout(() => setShowContent(true), 500);
    setTimeout(() => setShowStats(true), 3000);
    setTimeout(() => setShowCTA(true), 4000);
  }, []);
  
  const handleEnter = () => {
    // Animate exit and navigate
    navigate('/home');
  };
  
  const scrollToContent = () => {
    window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    
    // Simulate subscription (in production, integrate with Mailchimp/ConvertKit)
    try {
      // Using Formspree for email capture
      const response = await fetch('https://formspree.io/f/mojnlvad', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email, 
          source: 'Landing Page Lead Capture',
          timestamp: new Date().toISOString()
        })
      });
      
      if (response.ok) {
        setIsSubscribed(true);
        setEmail('');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1929] text-white overflow-hidden" data-testid="landing-page">
      {/* Background */}
      <CyberGrid />
      
      {/* Main content - Full screen intro */}
      <div 
        ref={containerRef}
        className="min-h-screen flex flex-col items-center justify-center relative px-4"
      >
        {/* Floating icons - positioned around */}
        <FloatingIcon icon={Shield} label="Proteção" delay={2.5} position="top-[15%] left-[10%] md:left-[15%]" />
        <FloatingIcon icon={Lock} label="Segurança" delay={2.7} position="top-[20%] right-[10%] md:right-[15%]" />
        <FloatingIcon icon={Eye} label="Monitoramento" delay={2.9} position="bottom-[25%] left-[8%] md:left-[12%]" />
        <FloatingIcon icon={Zap} label="Performance" delay={3.1} position="bottom-[30%] right-[8%] md:right-[12%]" />
        
        {/* Logo and brand */}
        <AnimatedShield isVisible={showContent} />
        
        {/* Brand name with typing effect */}
        <motion.div
          className="mt-8 text-center"
          initial={{ opacity: 0 }}
          animate={showContent ? { opacity: 1 } : {}}
          transition={{ delay: 0.5 }}
        >
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-4">
            {showContent && (
              <>
                <span className="text-white">DMM </span>
                <span className="text-[#10b981]">
                  <TypeWriter text="Security" delay={1500} speed={100} />
                </span>
              </>
            )}
          </h1>
          
          <motion.p
            className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={showContent ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 2.5 }}
          >
            Eleve a maturidade de segurança da sua empresa com inteligência em riscos e compliance
          </motion.p>
        </motion.div>
        
        {/* Stats section */}
        <AnimatePresence>
          {showStats && (
            <motion.div
              className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <StatCounter value="+300" suffix="%" label="Aumento em Ransomware" delay={0} />
              <StatCounter value="70" suffix="%" label="Redução em Auditorias" delay={200} />
              <StatCounter value="25" label="Controles PPSI 2.0" delay={400} />
              <StatCounter value="100" suffix="%" label="Compliance Garantido" delay={600} />
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* CTA Button */}
        <AnimatePresence>
          {showCTA && (
            <motion.div
              className="mt-16 flex flex-col items-center gap-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <motion.button
                onClick={handleEnter}
                className="group relative px-10 py-5 bg-gradient-to-r from-[#10b981] to-[#059669] rounded-xl font-bold text-lg overflow-hidden"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                data-testid="enter-site-btn"
              >
                {/* Button glow effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  animate={{
                    x: ['-100%', '100%'],
                  }}
                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                />
                <span className="relative flex items-center gap-3">
                  Explorar Soluções
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </motion.button>
              
              {/* Scroll indicator */}
              <motion.button
                onClick={scrollToContent}
                className="flex flex-col items-center gap-2 text-gray-500 hover:text-white transition-colors"
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <span className="text-sm">Ou role para descobrir mais</span>
                <ChevronDown className="w-5 h-5" />
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Second section - Why DMM */}
      <div className="min-h-screen bg-gradient-to-b from-[#0a1929] to-[#0f2535] px-4 py-20">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block px-4 py-2 bg-[#10b981]/10 text-[#10b981] rounded-full text-sm font-semibold mb-4">
              Por que a DMM?
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Sua parceira estratégica em{' '}
              <span className="text-[#10b981]">Segurança Digital</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-3xl mx-auto">
              Combinamos consultoria especializada com tecnologia proprietária para entregar resultados mensuráveis
            </p>
          </motion.div>
          
          {/* Features grid */}
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: 'Consultoria Especializada',
                description: 'Metodologia comprovada em ISO 27001, PPSI, LGPD e gestão de riscos corporativos',
                color: 'from-blue-500 to-cyan-500'
              },
              {
                icon: Lock,
                title: 'Plataforma SmartAudit',
                description: 'Tecnologia proprietária para gestão integrada de frameworks e cálculo automático de riscos',
                color: 'from-emerald-500 to-teal-500'
              },
              {
                icon: Zap,
                title: 'Resultados Rápidos',
                description: 'Redução de 70% no tempo de auditorias e visão executiva em tempo real',
                color: 'from-orange-500 to-amber-500'
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="relative group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#10b981]/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative p-8 border border-white/10 rounded-2xl bg-white/5 backdrop-blur-sm hover:border-[#10b981]/50 transition-colors">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6`}>
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
          
          {/* Lead Capture Form */}
          <motion.div
            className="mt-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="relative max-w-2xl mx-auto">
              {/* Glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-[#10b981] via-cyan-500 to-[#10b981] rounded-2xl blur-lg opacity-30 animate-pulse" />
              
              <div className="relative bg-[#0f2535] border border-white/10 rounded-2xl p-8 md:p-10">
                <div className="text-center mb-6">
                  <motion.div
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#10b981]/10 rounded-full text-[#10b981] text-sm font-medium mb-4"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Sparkles className="w-4 h-4" />
                    Conteúdo Exclusivo
                  </motion.div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-2">
                    Receba insights de segurança
                  </h3>
                  <p className="text-gray-400">
                    Alertas de vulnerabilidades, dicas de compliance e tendências direto no seu email
                  </p>
                </div>
                
                {isSubscribed ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex items-center justify-center gap-3 py-4 px-6 bg-[#10b981]/20 rounded-xl text-[#10b981]"
                  >
                    <Check className="w-6 h-6" />
                    <span className="font-semibold">Obrigado! Você receberá nossos conteúdos em breve.</span>
                  </motion.div>
                ) : (
                  <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Seu melhor email"
                        required
                        className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-[#10b981] focus:border-transparent transition-all"
                        data-testid="lead-email-input"
                      />
                    </div>
                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-8 py-4 bg-gradient-to-r from-[#10b981] to-[#059669] rounded-xl font-bold text-white hover:shadow-lg hover:shadow-[#10b981]/25 transition-all disabled:opacity-50"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      data-testid="lead-submit-btn"
                    >
                      {isSubmitting ? 'Inscrevendo...' : 'Inscrever-se'}
                    </motion.button>
                  </form>
                )}
                
                <p className="text-center text-xs text-gray-500 mt-4">
                  Respeitamos sua privacidade. Cancele quando quiser.
                </p>
              </div>
            </div>
          </motion.div>
          
          {/* Final CTA */}
          <motion.div
            className="mt-20 text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <motion.button
              onClick={handleEnter}
              className="group inline-flex items-center gap-3 px-12 py-6 bg-gradient-to-r from-[#10b981] to-[#059669] rounded-xl font-bold text-xl hover:shadow-2xl hover:shadow-[#10b981]/25 transition-all"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="explore-btn"
            >
              Comece Agora
              <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
            </motion.button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
